import React from 'react';
import {
  CheckCircle,
  Clock,
  MapPin,
  ShieldCheck,
  Workflow,
  Wrench,
} from 'lucide-react';

/**
 * Canonical Chain-of-Custody stages — single source of truth.
 *
 * The full 11-stage canonical sequence powers operational batches.
 * The compact 7-stage view is used for capability demos and snapshot cards.
 */
export const CANONICAL_STAGES = [
  { key: 'hatchery',     label: 'Hatchery'              },
  { key: 'farming',      label: 'Farming'               },
  { key: 'harvest',      label: 'Harvest'               },
  { key: 'quality_check',label: 'Quality Inspection'    },
  { key: 'processing',   label: 'Processing'            },
  { key: 'packaging',    label: 'Packaging'             },
  { key: 'cold_storage', label: 'Cold Storage'          },
  { key: 'transport',    label: 'Transport'             },
  { key: 'distribution', label: 'Distribution'          },
  { key: 'retail',       label: 'Retail'                },
  { key: 'consumer',     label: 'Consumer Verification' },
];

/**
 * Empty-state mode shows every canonical stage as a pending container so the
 * structure (timeline + fields) is preserved without injecting demo data.
 */const isoToLocal = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

/**
 * Inline stage row — every stage card visibly shows timestamp,
 * location, status, verification. No click required.
 */
const StageRow = ({ stage, ev, isLast, manuallyCorrected, correction }) => {
  const recorded = !!ev || manuallyCorrected;
  // Tooltip body: full audit context for manually-resolved stages so any
  // auditor / investor can see reason + corrective action + operator +
  // timestamp without leaving the timeline.
  const correctionTooltip = manuallyCorrected && correction
    ? [
        `Resolution: ${correction.reason || '—'}`,
        `Corrective action: ${correction.corrective_action || '—'}`,
        `Operator: ${correction.user || '—'}${correction.user_role ? ` (${correction.user_role})` : ''}`,
        `Timestamp: ${isoToLocal(correction.timestamp)}`,
      ].join('\n')
    : undefined;
  return (
    <li
      data-testid={`coc-stage-${stage.key}`}
      data-resolution={manuallyCorrected ? 'manual' : (recorded ? 'live' : 'pending')}
      className="relative pl-10 sm:pl-12 pb-5 sm:pb-6 last:pb-0"
    >
      {/* connector line */}
      {!isLast && (
        <span
          aria-hidden
          className={`absolute left-[14px] sm:left-[17px] top-7 bottom-0 w-0.5 ${
            manuallyCorrected ? 'bg-amber-300' : (recorded ? 'bg-emerald-300' : 'bg-slate-200')
          }`}
        />
      )}
      {/* node */}
      <span
        aria-hidden
        className={`absolute left-0 top-0 w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs font-semibold ${
          manuallyCorrected
            ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-200'
            : recorded
              ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-200'
              : 'bg-slate-100 text-slate-400 ring-2 ring-slate-200'
        }`}
      >
        {recorded ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> : '•'}
      </span>

      <div
        className={`rounded-lg p-3 sm:p-4 border ${
          manuallyCorrected
            ? 'bg-amber-50/40 border-amber-200'
            : recorded
              ? 'bg-white border-slate-200'
              : 'bg-slate-50 border-dashed border-slate-200'
        }`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <p className="text-sm sm:text-base font-semibold text-slate-900">
            {stage.label}
          </p>
          {recorded ? (
            <span
              title={correctionTooltip}
              data-testid={`coc-stage-${stage.key}-badge`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                manuallyCorrected
                  ? 'bg-amber-50 text-amber-800 border border-amber-300 cursor-help'
                  : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              }`}
            >
              {manuallyCorrected ? <Wrench className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
              {manuallyCorrected ? 'Manually Resolved' : 'Verified'}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 rounded-full text-[10px] sm:text-xs font-medium">
              <Clock className="w-3 h-3" /> Pending
            </span>
          )}
        </div>

        {manuallyCorrected && correction && (
          <div
            data-testid={`coc-stage-${stage.key}-resolution`}
            className="mt-2 rounded-md border border-amber-200/70 bg-amber-50/60 px-2.5 py-2 text-[11px] sm:text-xs text-amber-900 leading-snug"
          >
            <p className="font-semibold uppercase tracking-wider text-[9px] sm:text-[10px] text-amber-700 mb-1">
              Verified Operator Resolution
            </p>
            <p>
              <span className="font-medium">Reason:</span> {correction.reason || '—'}
            </p>
            <p>
              <span className="font-medium">Corrective action:</span> {correction.corrective_action || '—'}
            </p>
            <p className="mt-1 text-amber-800/80">
              Resolved by <span className="font-semibold">{correction.user || 'operator'}</span>
              {correction.user_role ? ` (${correction.user_role})` : ''}
              {' · '}{isoToLocal(correction.timestamp)}
            </p>
            <p className="mt-0.5 text-[10px] text-amber-700/80 italic">
              Resolved by authorized operator with full audit trail.
            </p>
          </div>
        )}

        {recorded && !manuallyCorrected ? (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 text-xs sm:text-sm">
            <Field icon={Clock} label="Timestamp" value={isoToLocal(ev?.timestamp)} />
            <Field icon={MapPin} label="Location" value={typeof ev?.location === 'string' ? ev.location : ev?.location?.name} />
            <Field icon={Workflow} label="Status" value={ev?.status} />
            <Field icon={ShieldCheck} label="Verification" value={ev?.verification || ev?.verification_status} />
          </div>
        ) : !recorded ? (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 text-xs sm:text-sm">
            <Field icon={Clock} label="Timestamp" value={null} />
            <Field icon={MapPin} label="Location" value={null} />
            <Field icon={Workflow} label="Status" value={null} />
            <Field icon={ShieldCheck} label="Verification" value={null} />
          </div>
        ) : null}
      </div>
    </li>
  );
};

const Field = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-1.5 min-w-0">
    <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
    <div className="min-w-0">
      <span className="text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
      <p className="text-xs sm:text-sm text-slate-800 break-words">{value || '—'}</p>
    </div>
  </div>
);

/**
 * Build a stage-keyed map from live event records.
 * Works with either explicit `stage_key` or `event_type` mapped to canonical key.
 */
const buildStageMap = (events) => {
  const m = {};
  for (const ev of events || []) {
    const k = (ev.stage_key || ev.event_type || '').toLowerCase().replace(/\s+/g, '_');
    if (CANONICAL_STAGES.find((s) => s.key === k)) m[k] = ev;
  }
  return m;
};

const buildResolvedKeys = (corrections) =>
  new Set((corrections || []).filter((c) => c.status === 'resolved').map((c) => c.missing_stage_key));

// Quick lookup so each StageRow can render the correction body inline +
// in the tooltip. If multiple corrections target the same stage, we use the
// newest (corrections are sorted newest-first server-side).
const buildResolvedByKey = (corrections) => {
  const m = {};
  for (const c of corrections || []) {
    if (c.status !== 'resolved') continue;
    if (!m[c.missing_stage_key]) m[c.missing_stage_key] = c;
  }
  return m;
};

/**
 * ChainOfCustodyTimeline
 *
 * @param {Array}   events       Live supply-chain event records
 * @param {Array}   corrections  Manual-correction records (resolved → counts as recorded)
 * @param {Boolean} forceDemo    Deprecated. Retained for prop compatibility; ignored.
 * @param {String}  title        Section heading (default: "Chain of Custody")
 * @param {Node}    actionSlot   Optional right-aligned action node (e.g. a link/button)
 */
const ChainOfCustodyTimeline = ({
  events = [],
  corrections = [],
  forceDemo: _forceDemo = false, // eslint-disable-line no-unused-vars
  title = 'Chain of Custody',
  actionSlot = null,
}) => {
  const stageMap = buildStageMap(events);
  const resolvedKeys = buildResolvedKeys(corrections);
  const resolvedByKey = buildResolvedByKey(corrections);
  const recordedCount = CANONICAL_STAGES.filter(
    (s) => stageMap[s.key] || resolvedKeys.has(s.key),
  ).length;
  const manualCount = CANONICAL_STAGES.filter(
    (s) => !stageMap[s.key] && resolvedKeys.has(s.key),
  ).length;
  const liveCount = recordedCount - manualCount;
  const isEmpty = recordedCount === 0;

  return (
    <div data-testid="chain-of-custody-timeline">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3 sm:mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Workflow className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
              {title}
            </h2>
            {isEmpty ? (
              <span
                data-testid="coc-empty-badge"
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-full text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Pending Live Batch Data
              </span>
            ) : (
              <span
                data-testid="coc-live-badge"
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-emerald-800"
              >
                Live Data
              </span>
            )}
          </div>
          {!isEmpty && (
            <p className="text-xs sm:text-sm text-slate-600 mt-1 max-w-2xl">
              {recordedCount} of {CANONICAL_STAGES.length} stages recorded for this batch
              {manualCount > 0 && (
                <>
                  {' '}·{' '}
                  <span className="text-emerald-700 font-medium">{liveCount} live-captured</span>
                  {' · '}
                  <span className="text-amber-700 font-medium">{manualCount} manually resolved</span>
                </>
              )}
              .
            </p>
          )}
        </div>
        {actionSlot && <div className="flex-shrink-0">{actionSlot}</div>}
      </div>

      {isEmpty && (
        <div
          data-testid="coc-empty-state"
          className="mb-4 sm:mb-5 bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3 sm:p-4"
        >
          <p className="text-xs sm:text-sm text-slate-500">
            No chain of custody events recorded yet.
          </p>
        </div>
      )}

      {/* Timeline */}
      <ol className="relative">
        {CANONICAL_STAGES.map((stage, i) => (
          <StageRow
            key={stage.key}
            stage={stage}
            ev={stageMap[stage.key]}
            manuallyCorrected={!stageMap[stage.key] && resolvedKeys.has(stage.key)}
            correction={resolvedByKey[stage.key]}
            isLast={i === CANONICAL_STAGES.length - 1}
          />
        ))}
      </ol>
    </div>
  );
};

export default ChainOfCustodyTimeline;
